import { Box, Chip, Paper, Typography } from '@mui/material';
import useDateTimeFormat from '../../../hooks/useDateTimeFormat';
import { Visit } from '../../../types/Visit';

interface VisitHistoryOverviewProps {
  currentVisit?: Visit | null;
}

const fieldLabelSx = {
  fontSize: 12,
  fontWeight: 600,
  color: '#94a3b8',
  textTransform: 'uppercase',
} as const;

const fieldValueSx = {
  fontSize: 15,
  fontWeight: 600,
  color: '#1e293b',
  lineHeight: 1.25,
  wordBreak: 'break-word',
} as const;

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const toDisplayDate = (value: unknown, toStandardDate: (date: any) => any): string => {
  if (!value) return '-';
  const formatted = toStandardDate(value);
  return formatted ? String(formatted) : '-';
};

const toDisplayNumber = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return toDisplayValue(value);
};

export default function VisitHistoryOverview({ currentVisit }: VisitHistoryOverviewProps) {
  const { toStandardDate, withTimeMode } = useDateTimeFormat();

  const fleetValue = toDisplayValue(currentVisit?.poolName ?? currentVisit?.poolName);

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase' }}>Device Overview</Typography>
        <Chip
          label='Visit History'
          sx={{
            bgcolor: '#dbeafe',
            color: '#1e3a8a',
            fontWeight: 700,
            textTransform: 'uppercase',
            borderRadius: 20,
          }}
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          backgroundColor: '#fff !important',
          border: '1px solid #d8dee8',
          borderRadius: 2,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '1.15fr 1.15fr 0.75fr 0.75fr 1.2fr 1.2fr 0.7fr',
          },
          '& > div': {
            backgroundColor: '#fff !important',
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>Device ID</Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {toDisplayValue(currentVisit?.deviceId)}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>Serial Number</Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {toDisplayValue(currentVisit?.serialNumber)}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>Station</Typography>
          <Typography sx={fieldValueSx}>{toDisplayValue(currentVisit?.stationIataCode)}</Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>Fleet</Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fleetValue}</Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>{withTimeMode('First Seen')}</Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {toDisplayDate(currentVisit?.firstSeenDateTime, toStandardDate)}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2, borderRight: { md: '1px solid #d8dee8' }, borderBottom: { xs: '1px solid #d8dee8', md: 'none' } }}>
          <Typography sx={fieldLabelSx}>{withTimeMode('Last Seen')}</Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {toDisplayDate(currentVisit?.lastSeenDateTime, toStandardDate)}
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 2 }}>
          <Typography sx={{ ...fieldLabelSx, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Total Sightings</Typography>
          <Typography sx={fieldValueSx}>{toDisplayNumber(currentVisit?.totalSightings)}</Typography>
        </Box>
      </Paper>
    </Box>
  );
}
